<template>
  <div
    ref="toolbarRoot"
    :class="[$style.toolbar, { [$style.toolbar_narrow]: isToolbarNarrow }]"
  >
    <div 
      ref="toolbarLeftRoot" 
      :class="$style.toolbar__left"
    >
      <div
        v-if="uiConfig.ui.includes('yearSwitch')"
        :class="$style.chart_toolbox"
      >
        <div :class="$style.year_switch">
          <vgp-icon-button
            :icon="{ name:'arrow-left', type: 'bold' }"
            size="small"
            :disabled="toolbarYearSwitchData.minYear === toolbarYearSwitchData.currentYear"
            @onClick="setCurrent(-1)"
          />

          <div :class="$style.current">
            {{ toolbarYearSwitchData.currentYear }} {{ locales('year') }}
          </div>

          <vgp-icon-button
            :icon="{ name:'arrow-right', type: 'bold' }"
            size="small"
            :disabled="toolbarYearSwitchData.maxYear === toolbarYearSwitchData.currentYear"
            @onClick="setCurrent(1)"
          />
        </div>
      </div>
      <div
        v-if="uiConfig.ui.includes('expandCollapse')"
        :class="[$style.expand_collapse_buttons, $divider.right_divider]"
      >
        <tooltip
          :class="$style.tooltip_wrapper"
          :content="locales('reports_table_toolbar_expand')"
          :position="'bottom-start'"
          :interactive="true"
          :with-animation="false"
        >
          <template #body>
            <vgp-icon-button
              size="small"
              :colors="buttonColors"
              :icon="{ name:'expand-1', size: 20 }"
              @onClick="$emit('onClickExpand')"
            />
          </template>
        </tooltip>
        <tooltip
          :class="$style.tooltip_wrapper"
          :content="locales('reports_table_toolbar_collaps')"
          :position="'bottom-start'"
          :interactive="true"
          :with-animation="false"
        >
          <template #body>
            <vgp-icon-button
              size="small"
              :colors="buttonColors"
              :icon="{ name:'collaps-1', size: 20 }"
              @onClick="$emit('onClickCollapse')"
            />
          </template>
        </tooltip>
        <div :class="$divider.divider" />
      </div>
      <div
        v-if="uiConfig.ui.includes('reportModeSelection')"
        :class="$divider.right_divider"
      >
        <toolbar-dropdown
          :select-label="locales('report_representation')"
          :menu="reportModeSelectionList"
          :disabled="toolbarDisabled"
          @onSelect="data => $emit('onSelectReportViewMode', data)"
        />
        <div :class="$divider.divider" />
      </div>
      <div
        v-if="uiConfig.ui.includes('grouping')"
        :class="$divider.right_divider"
      >
        <grouping-selection
          :group-list="groupList"
          :selected-list="selectedGroupList"
          @onSelectGroup="data => $emit('onSelectGroup', data)"
        />
        <div :class="$divider.divider" />
      </div>
      <div
        v-if="uiConfig.ui.includes('range') && rangeValue.length"
        :class="$divider.right_divider"
      >
        <div :class="$style.range_value">
          {{ rangeValue }}
        </div>
        <div v-if="uiConfig.ui.includes('calculationSelection')"
             :class="$divider.divider" />
      </div>
      <tooltip
        :class="$style.tooltip_wrapper"
        :content="tooltipTextForCalculationModeDropdown"
        :position="'top'"
        :hide="!isToolbarNarrow && tooltipTextForCalculationModeDropdown !== ''"
      >
        <template #body>
          <toolbar-dropdown
            v-if="uiConfig.ui.includes('calculationSelection')"
            :class="$style.calculation_select"
            :select-label="locales('reports_pages_model_of_calc')"
            :menu="dropMenu"
            :disabled="toolbarDisabled"
            :responsiveSize="isToolbarNarrow"
            @onSelect="data => $emit('onSelectDropdown', data)"
          />
        </template>
      </tooltip>
    </div>
    <div 
      ref="toolbarRightRoot" 
      :class="$style.toolbar__right"
    >
      <filter-button
        v-if="uiConfig.ui.includes('filter')"
        :delimiter-left="false"
        :delimiter-right="hasAccessToExport"
      />
      <tooltip
        v-if="uiConfig.ui.includes('export') && hasAccessToExport"
        :class="$style.tooltip_wrapper"
        :content="tooltipContent"
        :position="'bottom-end'"
        :interactive="true"
        :with-animation="false"
      >
        <template #body>
          <vgp-icon-button
            size="small"
            :colors="buttonColors"
            :disabled="toolbarDisabled"
            :label="locales('common_export')"
            :icon="{ name:'link-2', size: 20 }"
            :style="{marginLeft: '4px'}"
            @onClick="onClickExport"
          />
        </template>
      </tooltip>
    </div>
  </div>
</template>

<script>
import vgpIconButton from '$$vueCmp/globalButton/withIcon/iconButton.vue';
import toolBarUiConfig from './toolBarUiConfig';
import filterButton from '../../filter/components/filterButton.vue';
import toolbarDropdown from './toolbarDropdown.vue';
import tooltip from '../../common/VueComponents/tooltips/tooltip.vue';
import rights from '../../../components/rights/index';
import groupingSelection from './toolbarGroupingDropdown.vue';

export default {
  components: {
    vgpIconButton,
    filterButton,
    toolbarDropdown,
    tooltip,
    groupingSelection,
  },
  props: {
    iconName: {
      type: String,
      required: false,
      default: 'report',
    },
    iconColor: {
      type: String,
      required: false,
      default: '#00564C',
    },
    dropMenu: { type: Array, default: () => [] },
    rangeValue: { type: String, required: false, default: '' },
    toolbarDisabled: { type: Boolean, required: false, default: false },
    toolbarYearSwitchData: {
      type: Object,
      required: false,
      default: () => ({
        currentYear: new Date().getFullYear(),
        minYear: new Date().getFullYear(),
        maxYear: new Date().getFullYear(),
      }),
    },
    reportTitle: { type: String, required: true, default: '' },
    activeReportViewMode: { type: String, required: false, default: 'graphic' },
    groupList: { type: Array, default: () => [] },
    selectedGroupList: { type: Array, default: () => [] },
  },
  emits: [
    'setYear',
    'onSelectDropdown',
    'onClickExport',
    'onClickExpand',
    'onClickCollapse',
    'onSelectGroup',
    'onSelectReportViewMode',
  ],
  data() {
    return {
      locales: __,
      buttonColors: {
        color: '#333333',
        labelColor: '#191919',
        activeColor: '#11519A',
        hoverBg: '#F2F2F2',
        activeBg: '#D0E0F2',
        activeHoverBg: '#A1C1E6',
      },
      toolbarContentWidth: 0,
      isToolbarNarrow: false,
    };
  },
  computed: {
    mode() {
      if (this.$route?.params?.type === 'portfolio') {
        return 'table';
      }

      return this.activeReportViewMode;
    },
    tooltipContent() {
      return this.mode === 'table' ? __('header_toolbar_export_tooltip_report_table') : __('header_toolbar_export_tooltip_graphic');
    },
    hasAccessToExport() {
      return rights.account.hasRight('export');
    },
    reportModeSelectionList() {
      return [
        {
          type: 'item',
          name: 'graphic',
          text: __('graphic_view_option'),
          isActive: this.activeReportViewMode === 'graphic',
        },
        {
          type: 'item',
          name: 'table',
          text: __('table'),
          isActive: this.activeReportViewMode === 'table',
        },
      ];
    },
    uiConfig() {
      const path = this.$route.path.split('/').slice(1).join('_'); // view like name_of_router

      return toolBarUiConfig.getConfig(path, this.activeReportViewMode);
    },
    tooltipTextForCalculationModeDropdown() {
      const active = this.dropMenu.find(m => m.isActive);

      return active?.text || '';
    },
    leftSidebarWidth() {
      return this.$store.getters.leftSidebarWidth;
    }
  },
  // mounted() {
  //   const path = this.$route.path.split('/').slice(1).join('_'); // view like name_of_router

  //   this.uiConfig = toolBarUiConfig.getConfig(path, this.activeReportViewMode);
  // },
  watch: {
    selectedGroupList: {
      handler() {
        this.$nextTick(() => {
          this.calculateToolbarContentWidth();
          this.updateToolbarWidthState();
        })
      },
      deep: true,
    },
    leftSidebarWidth: {
      handler() {
        this.updateToolbarWidthState();
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateToolbarContentWidth();
      this.updateToolbarWidthState();
    })
    
    window.addEventListener('resize', this.onResizeWindow);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResizeWindow);
  },
  methods: {
    onResizeWindow() {
      this.updateToolbarWidthState();
    },
    calculateToolbarContentWidth() {
      const toolbarRoot = this.$refs.toolbarRoot;

      if (!toolbarRoot) {
        return;
      }

      let contentWidth = 32;

      const children = Array.from(toolbarRoot.children || []);

      children.forEach((child, index) => {
        const rect = child.getBoundingClientRect();
        contentWidth += rect.width;
      });

      this.toolbarContentWidth = contentWidth;
    },
    updateToolbarWidthState() {
      const leftSidebarWidth = this.$store.getters.leftSidebarWidth;
      const containerWidth = document.documentElement.clientWidth - leftSidebarWidth;

      this.isToolbarNarrow = this.toolbarContentWidth > containerWidth;
    },
    setCurrent(delta) {
      const result = this.toolbarYearSwitchData.currentYear + delta;

      this.$emit('setYear', result);
    },
    onClickExport() {
      this.$emit('onClickExport');
    },
  },
};
</script>

<style module src='./../less/reportsMain.less'></style>
<style module="$divider" lang="less">
  .right_divider {
    display: flex;
    align-items: center;
    .divider {
      margin: 0 4px;
      width: 1px;
      height: 16px;
      background: #E5E5E5;
    }
  }
</style>
